import React from 'react';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';

import { VideoThumbnail } from './video-thumbnail';
import { ImageThumbnail } from './image-thumbnail';
import { Timeline, useReels } from '../../../../../contexts/reels';
import { GameDocumentContext } from '../../../../../contexts/game-document';
import { GetTaskContentByTaskId } from '../../../../../utils/game-engine/player';
import { useGetPlayerStateByTeamCode } from '../../../../../hooks/use-get-player-state';

interface MediaCardProps extends React.PropsWithChildren {
  item: {
    id: string;
    url: string;
    type: string;
  };
}

export const MediaCard: React.FC<MediaCardProps> = ({ item, children }) => {
  const { timeline, handleChange } = useReels();
  const isAdded = timeline.find((timeline) => timeline.id === item.id);

  const onAddOrRemoveTimeline = React.useCallback(
    (duration: number, thumbnail?: string) => {
      if (isAdded) {
        const getId = timeline.findIndex((timeline) => timeline.id === item.id);
        if (getId !== -1) {
          const result = cloneDeep(timeline);
          result.splice(getId, 1);
          handleChange({ timeline: result });
        }
      } else {
        const newMedia: Timeline = {
          id: item.id,
          type: item.type as Timeline['type'],
          duration_secs: duration,
          url: item.url,
          ...(thumbnail !== undefined && {
            thumbnail
          })
        };
        const result = cloneDeep(timeline);
        handleChange({ timeline: [...result, newMedia] });
      }
    },
    [isAdded, timeline, item, handleChange]
  );

  return (
    <div
      className={'reels-tasks-media-card'}
      style={{
        ...(isAdded && {
          border: '3px solid #4698CB'
        })
      }}>
      {item.type === 'image' && (
        <ImageThumbnail
          url={item.url}
          isAdded={isAdded !== undefined}
          onAddOrRemoveTimeline={onAddOrRemoveTimeline}
        />
      )}

      {item.type === 'video' && (
        <VideoThumbnail
          key={item.id}
          videoId={item.id}
          videoUrl={item.url}
          isAdded={isAdded !== undefined}
          onAddOrRemoveTimeline={onAddOrRemoveTimeline}
        />
      )}

      {isAdded && (
        <div
          className={
            'd-flex align-items-center justify-content-center p-1 position-absolute'
          }
          style={{
            top: '8px',
            left: '8px',
            fontSize: '12px',
            borderRadius: '4px',
            backgroundColor: '#4698CB'
          }}>
          Added
        </div>
      )}

      <div
        className={
          'd-flex align-items-center justify-content-center bg-white p-1 position-absolute'
        }
        style={{ top: '8px', right: '8px', borderRadius: '4px' }}>
        <span
          className="material-symbols-outlined"
          style={{
            color: item.type === 'video' ? '#14BF8B' : '#0a0a0a',
            fontSize: '16px'
          }}>
          {item.type === 'video' ? 'smart_display' : 'image'}
        </span>
      </div>

      {children}
    </div>
  );
};

export const MediaGrid: React.FC<{
  selectedTeam: string;
  selectedTask: string;
}> = ({ selectedTeam, selectedTask }) => {
  const { gameCode } = useParams();
  const [gameDocument] = React.useContext(GameDocumentContext);

  const { playerStates, isLoading } = useGetPlayerStateByTeamCode(gameCode);

  const media = React.useMemo(() => {
    if (!playerStates) return [];
    const result: { id: string; url: string; type: string }[] = [];

    playerStates
      .filter(
        (player) =>
          player.playerState?.teamCode === selectedTeam ||
          selectedTeam === 'All Teams'
      )
      .forEach((player) => {
        const tasks = player.playerState?.tasks || [];

        tasks
          .filter(
            (task) => task.id === selectedTask || selectedTask === 'All Tasks'
          )
          .forEach((task) => {
            if (!task.taskContentFormAnswers) return;
            task.taskContentFormAnswers.forEach((formAnswer) => {
              const taskContent = GetTaskContentByTaskId(
                gameDocument.gameDocument!,
                task.id ?? ''
              );
              const form = taskContent?.forms?.find(
                (f) => f.id === formAnswer.formId
              );

              if (form && ['image', 'video'].includes(form.type)) {
                result.push({
                  id: `${player.playerState?.code}-${task.id}-${formAnswer.formId}`,
                  url: formAnswer.answers ? formAnswer.answers[0].value : '',
                  type: form.type
                });
              }
            });
          });
      });
    return result;
  }, [selectedTeam, selectedTask, playerStates]);

  return (
    <>
      {isLoading && (
        <div
          className={
            'd-flex flex-column gap-3 flex-grow-1 align-items-center justify-content-center'
          }>
          <span className={'loader'} role="status"></span>
          Load media content
        </div>
      )}

      {!isLoading && media.length > 0 && (
        <div className={'reels-tasks-media-grid-container'}>
          {media.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </>
  );
};
